import { inject, Injectable } from '@angular/core';
import { EMPTY, forkJoin, Observable, of } from 'rxjs';
import { catchError, filter, first, map, switchMap, tap } from 'rxjs/operators';
import { FraudCheckService, LOCAL_STORAGE_FRAUD_KEY_DEPOSIT } from './fraud-check.service';
import { LOCAL_STORAGE_UBIDEX_KEY_DEPOSIT, UbidexService } from './ubidex.service';
import { LocalstorageService, PlatformService } from 'ngx-unificator/services';
import { WrSocketService } from './wr-socket.service';
import { WS_RESPONSE_TYPE } from '../enums/ws-response-type.enum';
import { UserService } from './user/user.service';

@Injectable({
  providedIn: 'root'
})
export class SocketSuccessHandlerService {
  private _fraud = inject(FraudCheckService);
  private _ubidex = inject(UbidexService);
  private _storage = inject(LocalstorageService);
  private _wrSocket = inject(WrSocketService);
  private _platform = inject(PlatformService);
  private _user = inject(UserService);

  private _isPending = false;


  /**
   * Starts listening to socket success and handle actions
   */
  initHandler(): void {
    if (!this._platform.isBrowser) return;

    this._user.auth$
      .pipe(
        filter(auth => !!auth),
        first(),
        switchMap(() =>
          this._wrSocket.depositSuccessNotify$.pipe(
            filter(() => !this._isPending),
            tap(() => this._isPending = true),
            switchMap((data: any) => this._handleDepositSuccess(data)),
            tap(() => this._isPending = false),
            catchError(err => {
              this._isPending = false;
              return EMPTY;
            })
          )
        )
      )
      .subscribe();
  }

  /**
   * Handles deposit success notification by processing seon and ubidex requests
   */
  private _handleDepositSuccess(data: any): Observable<any> {
    const shouldRunSeon = this._shouldHandleSeonRequest(data);
    const shouldRunUbidex = this._shouldHandleUbidexRequest(data);
    const requests = [];

    if (shouldRunSeon) {
      requests.push(this._executeSeonRequest().pipe(
        tap((resp) => {
          if (resp) {
            this._storage.clearItem(LOCAL_STORAGE_FRAUD_KEY_DEPOSIT)
          }
        })
      ));
    }

    if (shouldRunUbidex) {
      requests.push(this._executeUbidexRequest().pipe(
        tap((resp) =>  {
          if (resp) {
            this._ubidex.clearStorage();
          }
        })
      ));
    }

    if (requests.length === 0) {
      this._notifyDepositAccepted(data);
      return of(data);
    }

    return forkJoin(requests).pipe(
      tap(() => {
        this._notifyDepositAccepted(data);
      }),
      map(() => data),
      catchError(error => {
        console.log('Deposit success handling failed:', error);
        return EMPTY;
      })
    );
  }

  /**
   * Determines if Seon fraud request should be executed
   */
  private _shouldHandleSeonRequest(data: any): boolean {
    return this._fraud.isBodyFraudStorageLength(LOCAL_STORAGE_FRAUD_KEY_DEPOSIT) &&
      this._fraud.getUserIdFromDecodedString() === Number(data.playerId);
  }

  /**
   * Determines if Ubidex request should be executed
   */
  private _shouldHandleUbidexRequest(data: any): boolean {
    return this._ubidex.isStorageLength(LOCAL_STORAGE_UBIDEX_KEY_DEPOSIT) &&
      this._ubidex.getPlayerId() === +data.playerId;
  }

  private _executeSeonRequest(): Observable<any> {
    const fraudData = this._storage.get(LOCAL_STORAGE_FRAUD_KEY_DEPOSIT);

    return this._fraud.postFraudData(fraudData)
      .pipe(
        catchError(error => {
          console.log('Fraud check request failed:', error);
          return EMPTY;
        })
      );
  }

  private _executeUbidexRequest(): Observable<any> {
    return this._ubidex.sendDepositEvents()
      .pipe(
        catchError(error => {
          console.log('Ubidex request failed:', error);
          return EMPTY;
        })
      );
  }

  /**
   * Sends deposit accepted notification via socket
   */
  private _notifyDepositAccepted(data: any): void {
    this._wrSocket.socket.emit(WS_RESPONSE_TYPE.NOTIFY_ACCEPTED, {
      messageUid: data?.uid,
    });
  }
}
